import { EVENTS_CONSTANTS } from "config/constants";

const { EVENT_PERIOD_IDS } = EVENTS_CONSTANTS;

const getFullName = (...parts) => parts.filter(Boolean).join(" ").trim();

const getResponseData = response => response?.data?.data;

const SLOT_DURATION_MINUTES = 30;

const splitInactivityIntoSlots = (inactivity = []) =>
    inactivity.flatMap(block => {
        const start = block.time.start;
        const end = block.time.end;
        const slots = [];

        for (let slotStart = start; slotStart < end; slotStart += SLOT_DURATION_MINUTES) {
            slots.push({
                fecha: block.date,
                horaInicio: slotStart,
                horaFin: Math.min(slotStart + SLOT_DURATION_MINUTES, end),
                motivo: "Otras actividades"
            });
        }

        return slots;
    });

export const mapBaseResponse = response => ({
    success: response?.data?.state === 1,
    message: response?.data?.msg || null,
    responseCode: getResponseData(response)?.responseCode || null,
    description: getResponseData(response)?.descripcion || null,
    data: getResponseData(response)
});

export const mapCreateAdvisoryRequest = form => {
    const { adviser } = form.step1.part1;
    const { periodId, duration } = form.step1.part2;
    const { locationId, place } = form.step2.part1;
    const { publicByCampusIds, modalityId } = form.step2.part2.publicLoad.students;
    const isSingleDay = periodId === EVENT_PERIOD_IDS.ONLY_DAY;
    const selectedDuration = isSingleDay
        ? [duration.onlyDay]
        : duration.manyDays.time.map(item => ({
            ...item,
            date: duration.manyDays.date
        }));

    return {
        codigoAsesor: adviser.code,
        idCampusDetalleBase: place?.campus?.id || 0,
        idModalidad: modalityId,
        idUbicacion: locationId,
        fechaUnica: isSingleDay ? duration.onlyDay.date : null,
        fechaInicio: isSingleDay ? null : duration.manyDays.date.start,
        fechaFin: isSingleDay ? null : duration.manyDays.date.end,
        ambiente: locationId === 2 ? place?.environment?.envName || null : null,
        piso: locationId === 2 ? place?.environment?.floor || null : null,
        campusObjetivoIds: publicByCampusIds,
        duraciones: selectedDuration.map(item => ({
            idDia: isSingleDay ? null : item.dayId,
            horaInicio: item.time?.start ?? item.start,
            horaFin: item.time?.end ?? item.end,
            inactividad: splitInactivityIntoSlots(
                (item.time?.inactivity || item.inactivity || []).map(block => ({
                    ...block,
                    date: isSingleDay ? item.date : block.date
                }))
            )
        })),
        publicarAutomaticamente: true
    };
};

export const mapAdvisorySearchRequest = ({
    userCode,
    adviserCode,
    dateFrom,
    dateTo,
    campusId,
    modalityId
}) => ({
    codigoUsuario: userCode,
    codigoAsesor: adviserCode || null,
    fechaDesde: dateFrom ?? null,
    fechaHasta: dateTo ?? null,
    idCampus: campusId || null,
    idModalidad: modalityId || null
});

export const mapAdminReservationSearchRequest = ({
    userCode,
    dateFrom,
    dateTo,
    studentText,
    studentCode,
    adviserCode,
    campusId,
    modalityId,
    reservationState
}) => ({
    codigoUsuario: userCode,
    fechaDesde: dateFrom ?? null,
    fechaHasta: dateTo ?? null,
    textoAlumno: studentText || null,
    codigoAlumno: studentCode || null,
    codigoAsesor: adviserCode || null,
    idCampus: campusId || null,
    idModalidad: modalityId || null,
    estadoReserva: reservationState || null
});

export const mapAdvisorCalendarRequest = ({
    userCode,
    referenceDate,
    studentText,
    studentCode,
    onlyReserved,
    onlyAvailable
}) => ({
    codigoUsuario: userCode,
    fechaReferencia: referenceDate ?? null,
    textoAlumno: studentText || null,
    codigoAlumno: studentCode || null,
    soloReservados: onlyReserved ?? null,
    soloDisponibles: onlyAvailable ?? null
});

const mapReservationToUI = data => {
    const reservation = data.reservation || data;
    const isPresential = reservation.idUbicacionAtencion
        ? reservation.idUbicacionAtencion === 2
        : !reservation.esVirtual;

    return {
        id: reservation.idReserva,
        idUbicacionAtencion: reservation.idUbicacionAtencion,
        student: {
            id: reservation.codigoAlumno,
            name: reservation.nombreAlumno
        },
        timeStart: reservation.horaInicio,
        timeEnd: reservation.horaFin,
        attended: reservation.asistencia,
        zoom: reservation.enlaceZoom || null,
        isPresential,
        phone: reservation.celular || null,
        comment: reservation.comentario || null,
        quantity: {
            idx: 1,
            total: 1
        }
    };
};

const mapSlotToAppointment = ({ advisory, slot }) => {
    const slotReservations = slot.reserva
        ? [mapReservationToUI({
            advisory,
            reservation: slot.reserva
        })]
        : [];

    return {
        schedule: {
            appointmentId: advisory.idAsesoria,
            appointmentDateId: advisory.idAsesoriaFecha,
            date: slot.fecha,
            durationId: slot.idDuracion,
            start: slot.horaInicio,
            end: slot.horaFin,
            isPresential: advisory.idUbicacion === 2,
            place: advisory.idUbicacion === 2 ? {
                environment: slot.ambiente || null,
                campus: slot.campus || advisory.codigoCampus || null,
                address: slot.direccion || null,
                floor: slot.piso || null
            } : null
        },
        reservations: slotReservations,
        inactivities: slot.idInactividad
            ? [{
                id: slot.idInactividad,
                start: slot.horaInicio,
                end: slot.horaFin
            }]
            : []
    };
};

export const mapAdministrativeScheduleToUI = ({
    advisories,
    slotsByAdvisoryDate
}) => {
    const adviserGroups = new Map();

    advisories.forEach(advisory => {
        const adviserCode = advisory.codigoAsesor;
        const current = adviserGroups.get(adviserCode) || {
            adviser: {
                code: adviserCode,
                name: getFullName(advisory.nombresAsesor, advisory.apellidosAsesor)
            },
            appointments: []
        };
        const slots = slotsByAdvisoryDate[advisory.idAsesoriaFecha] || [];
        current.appointments.push(...slots.map(slot =>
            mapSlotToAppointment({ advisory, slot })
        ));
        adviserGroups.set(adviserCode, current);
    });

    return Array.from(adviserGroups.values());
};

export const mapAdvisorCalendarToUI = calendar => {
    const week = calendar?.semana || calendar || {};
    const adviser = {
        code: week.codigoAsesor,
        name: week.nombreAsesor
    };
    const appointmentsByDate = new Map();
    (week.bloques || []).forEach(block => {
        const isInactiveBlock = !block.idReserva &&
            !block.reservado &&
            !block.disponible &&
            typeof block.estadoTurno === "string" &&
            block.estadoTurno.toLowerCase().includes("actividad");
        const appointments = appointmentsByDate.get(block.fecha) || [];
        appointments.push({
            schedule: {
                appointmentId: block.idAsesoria,
                appointmentDateId: block.idAsesoriaFecha,
                date: block.fecha,
                durationId: null,
                start: block.horaInicio,
                end: block.horaFin,
                isPresential: !block.esVirtual,
                place: block.esVirtual ? null : {
                    environment: block.ambiente,
                    campus: block.campus,
                    address: block.direccion,
                    floor: block.piso
                }
            },
            reservations: block.idReserva
                ? [mapReservationToUI({
                    idReserva: block.idReserva,
                    codigoAlumno: block.codigoAlumno,
                    nombreAlumno: block.nombreAlumno,
                    horaInicio: block.horaInicio,
                    horaFin: block.horaFin,
                    asistencia: block.asistencia,
                    esVirtual: block.esVirtual
                })]
                : [],
            inactivities: isInactiveBlock
                ? [{
                    id: `emp-inact-${block.idAsesoriaFecha}-${block.horaInicio}-${block.horaFin}`,
                    start: block.horaInicio,
                    end: block.horaFin
                }]
                : []
        });
        appointmentsByDate.set(block.fecha, appointments);
    });
    return Array.from(appointmentsByDate.values()).map(appointments => ({
        adviser,
        appointments
    }));
};

export const mapReservationDetailToUI = detail => detail && ({
    id: detail.idReserva,
    appointmentDateId: detail.idAsesoriaFecha,
    student: {
        id: detail.codigoAlumno,
        name: detail.nombreAlumno
    },
    timeStart: detail.horaInicio,
    timeEnd: detail.horaFin,
    attended: detail.asistencia,
    zoom: detail.enlaceZoom || null,
    adviserZoom: detail.enlaceAsesor || null,
    zoomEnabled: detail.zoomHabilitado,
    isPresential: !detail.esVirtual,
    place: detail.esVirtual ? null : {
        environment: detail.ambiente,
        campus: detail.campus,
        address: detail.direccion,
        floor: detail.piso
    },
    phone: detail.celular || null,
    comment: detail.comentario || null,
    state: detail.estadoReserva,
    session: detail.sesion || null
});

export const mapReassignmentCandidateToUI = candidate => ({
    appointmentDateId: candidate.idAsesoriaFecha,
    appointmentId: candidate.idAsesoria,
    adviser: {
        id: candidate.codigoAsesor,
        name: getFullName(candidate.nombresAsesor, candidate.apellidosAsesor)
    },
    date: candidate.fecha,
    time: {
        start: candidate.horaInicio,
        end: candidate.horaFin
    },
    isPresential: candidate.idUbicacion === 2
});

export const mapAttendanceRequest = ({ userCode, attended }) => ({
    codigoUsuario: userCode,
    asistencia: attended
});

export const mapSessionRequest = ({
    userCode,
    reasonId,
    otherReason,
    observation,
    followUp,
    result,
    actualStart,
    actualEnd
}) => ({
    codigoUsuario: userCode,
    ...(reasonId !== undefined && { idMotivo: reasonId }),
    ...(otherReason !== undefined && { otroMotivo: otherReason }),
    ...(observation !== undefined && { observacion: observation }),
    ...(followUp !== undefined && { seguimiento: followUp }),
    ...(result !== undefined && { resultado: result }),
    ...(actualStart !== undefined && { horaInicioReal: actualStart }),
    ...(actualEnd !== undefined && { horaFinReal: actualEnd })
});
